import { SettingOutlined } from "@ant-design/icons";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { NavTile } from "../../../../../shared/frontend/ui/NavTiles";
import { NavigationOverview } from "../../../../../shared/frontend/navigation/NavigationOverview";
import { getFeatureAccess, type FeatureAccessResult } from "../app/auth/useFeatureAccess";
import { useAuth } from "../app/auth/useAuth";
import type { LocalizedText } from "../app/i18n/i18n";
import { FeatureLockModal } from "./FeatureLockModal";
import { navCardIconByKey } from "./navCardIcons";

export type NavCardItem = {
  path: string;
  titleKey: LocalizedText;
  descriptionKey?: LocalizedText;
  icon?: ReactNode;
  iconKey?: string;
  capabilityKey?: string;
};
export type NavCardGroup = { titleKey?: LocalizedText; items: NavCardItem[] };
export type NavCardGridProps = { title?: ReactNode; empty?: ReactNode; groups?: NavCardGroup[]; items?: NavCardItem[]; text: (t: LocalizedText) => string };

/**
 * Produktadapter: Zugriff, Sperr-Hinweis, Symbole und Navigation. Der gemeinsame Kit-Baustein
 * `NavTiles` besitzt Darstellung und Zeilenbudget für alle Navigationskarten.
 */
export function NavCardGrid({ title, empty, groups, items, text }: NavCardGridProps) {
  const nav = useNavigate();
  const auth = useAuth();
  const isOwnerOrAdmin = auth.hasRole("admin") || auth.hasPermission("billing.write");
  const [lockedModalAccess, setLockedModalAccess] = useState<FeatureAccessResult | null>(null);
  const resolved = useMemo(() => {
    const resolve = (item: NavCardItem): NavTile => {
      const access = item.capabilityKey ? getFeatureAccess(auth.info?.capabilities, item.capabilityKey, { isOwnerOrAdmin }) : null;
      return {
        key: item.path, title: text(item.titleKey), description: item.descriptionKey ? text(item.descriptionKey) : undefined,
        icon: item.icon ?? (item.iconKey ? navCardIconByKey[item.iconKey] : null) ?? <SettingOutlined />,
        locked: access?.isLocked ?? false, badge: access ? text(access.metadata.badgeText) : undefined,
        onActivate: () => { if (access?.isLocked) setLockedModalAccess(access); else nav(item.path); },
      };
    };
    return { groups: groups?.map((group, index) => ({ key: `group-${index}`, title: group.titleKey ? text(group.titleKey) : undefined, items: group.items.map(resolve) })), items: items?.map(resolve) };
  }, [groups, items, text, auth.info?.capabilities, isOwnerOrAdmin, nav]);
  return <><NavigationOverview title={title} empty={empty} {...resolved} /><FeatureLockModal open={lockedModalAccess !== null} onClose={() => setLockedModalAccess(null)} access={lockedModalAccess} text={text} /></>;
}
